import { useState } from 'react';
import { useSEO, generateSchemaOrg } from '../../../utils/seo';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: 'What is MTA Planner Pro and how does it work?',
    answer: 'MTA Planner Pro is AI-powered quoting software designed specifically for Australian tradies. Simply upload your building plans, and our AI automatically calculates material quantities, generates accurate quotes, and exports professional PDFs in under 5 minutes. It eliminates manual take-offs and reduces quoting time by over 90%.'
  },
  {
    question: 'How accurate are the AI-generated material take-offs?',
    answer: 'Our AI achieves 95%+ accuracy on material calculations by analyzing building plans using advanced computer vision. The system is trained on thousands of Australian construction projects and follows local building standards. You can always review and adjust quantities before finalizing quotes.'
  },
  {
    question: 'What types of trades does MTA Planner Pro support?',
    answer: 'MTA Planner Pro supports all major construction trades including plumbing, electrical, carpentry, tiling, painting, roofing, HVAC, and landscaping. The software adapts to your specific trade requirements and material lists.'
  },
  {
    question: 'How much does MTA Planner Pro cost?',
    answer: 'Plans start from $29/month for sole traders with unlimited quotes. We offer a 14-day free trial with no credit card required. Growing teams pay $79/month, and enterprise plans start at $199/month. All plans include AI quoting, GPS tracking, and team scheduling.'
  },
  {
    question: 'Can I try MTA Planner Pro before purchasing?',
    answer: 'Yes! We offer a completely free 14-day trial with full access to all features. No credit card required to start. You can generate unlimited quotes, test GPS tracking, and explore team scheduling during your trial period.'
  },
  {
    question: 'Does MTA Planner Pro work on mobile devices?',
    answer: 'Absolutely. MTA Planner Pro is fully responsive and works seamlessly on smartphones, tablets, and desktop computers. Most tradies use our mobile app on job sites to generate quotes, track team locations, and manage schedules on the go.'
  },
  {
    question: 'How long does it take to generate a quote?',
    answer: 'Most quotes are generated in under 5 minutes. Simply upload your plans, review the AI-calculated materials, adjust pricing if needed, and export a professional PDF. This is 90% faster than traditional manual quoting methods.'
  },
  {
    question: 'Can I customize quotes with my business branding?',
    answer: 'Yes! Every quote can be fully branded with your business logo, colors, contact details, and terms. You can also create custom templates for different project types and save frequently used material lists for faster quoting.'
  },
  {
    question: 'What file formats can I upload for material take-offs?',
    answer: 'MTA Planner Pro accepts PDF plans, JPG images, PNG files, and DWG/DXF CAD drawings. Our AI can process architectural plans, engineering drawings, site plans, and hand-drawn sketches.'
  },
  {
    question: 'Does MTA Planner Pro integrate with accounting software?',
    answer: 'Yes, we integrate with Xero, MYOB, and QuickBooks for seamless invoice syncing. Quotes can be converted to invoices with one click and automatically synced to your accounting platform.'
  },
  {
    question: 'What is the GPS tracking feature used for?',
    answer: 'GPS tracking lets you monitor your team\'s real-time locations, track job site arrivals, calculate travel time, and verify work hours. It\'s perfect for managing multiple crews across different sites and improving accountability.'
  },
  {
    question: 'Can multiple team members use one account?',
    answer: 'Yes! Growing Team and Enterprise plans support multiple users. Each team member gets their own login, and you can set permissions for quoting, scheduling, GPS tracking, and invoicing. Team chat is included for seamless communication.'
  },
  {
    question: 'Is my data secure and backed up?',
    answer: 'Absolutely. All data is encrypted with bank-level security (256-bit SSL), stored on Australian servers, and backed up daily. We comply with Australian privacy laws and never share your data with third parties.'
  },
  {
    question: 'What happens after my free trial ends?',
    answer: 'After your 14-day trial, you can choose a paid plan to continue using MTA Planner Pro. If you don\'t upgrade, your account remains active in read-only mode so you can access past quotes and data anytime.'
  },
  {
    question: 'Do you offer training or customer support?',
    answer: 'Yes! We provide free onboarding training, video tutorials, and 24/7 email support. Our Australian-based support team typically responds within 2 hours during business days. Live chat and phone support are available on Enterprise plans.'
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Generate FAQ Schema for SEO
  const faqSchema = generateSchemaOrg('FAQPage', {
    mainEntity: faqData.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  });

  useSEO({
    title: 'MTA Planner Pro - Frequently Asked Questions',
    description: 'Find answers to common questions about MTA Planner Pro, including pricing, features, accuracy, integrations, and support.',
    structuredData: faqSchema
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-2xl mb-6">
            <i className="ri-question-line text-3xl text-amber-600"></i>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about MTA Planner Pro. Can't find your answer? Contact our support team.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqData.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border-2 border-gray-100 overflow-hidden transition-all duration-300 hover:border-amber-200 hover:shadow-lg"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left transition-colors duration-200 hover:bg-gray-50"
                aria-expanded={openIndex === index}
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-8">
                  {item.question}
                </h3>
                <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xl text-amber-600"></i>
                </div>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6 pt-2">
                  <p className="text-gray-600 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Our Australian-based support team is here to help. Get answers within 2 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-black text-white font-semibold rounded-xl hover:bg-gray-800 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl mr-2"></i>
                Contact Support
              </a>
              <a
                href="tel:+611300123456"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl border-2 border-gray-200 hover:border-amber-400 hover:bg-amber-50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl mr-2"></i>
                1300 123 456
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}